import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { request } from "@/lib/request";
import { decode } from "@/lib/crypto";
import TimClient from "@/lib/timClient";
import TIM from "tim-js-sdk";

declare global {
  interface Window {
    Jessibuca: any;
  }
}

const DEFAULT_PLAT = "xr";
const ALL_PAGES = "__all__";
const LINK_TEST_TIMEOUT = 8000;
const LINK_TEST_CONCURRENCY = 3;
const IMAGE_LOAD_TIMEOUT = 8000;
const IMAGE_LOAD_CONCURRENCY = 6;

type LiveSort = "watchNumber" | "latency";
type ImageLoadStatus = "loading" | "valid" | "invalid";

type LinkTestStatus =
  | { status: "testing" }
  | { status: "available"; latency: number }
  | { status: "invalid"; reason: string }
  | { status: "restricted" };

interface LiveItem {
  id: string;
  groupId: string;
  hostName: string;
  title: string;
  liveImage: string;
  watchNumber: number;
  nplayFlv: string;
  plat: string;
  page?: string | number;
  update_time: string;
}

const normalizePage = (page: LiveItem["page"]) =>
  page === undefined || page === null
    ? ""
    : String(page).trim();

const getLiveKey = (live: LiveItem) =>
  `${live.plat}:${live.id}:${live.groupId}:${live.nplayFlv}:${live.liveImage}`;

interface PlatItem {
  name: string;
  lives?: LiveItem[];
  info?: any;
}

export default function Live() {
  const [plats, setPlats] = useState<PlatItem[]>([]);
  const [plat, setPlat] = useState(DEFAULT_PLAT);
  const [platInfo, setPlatInfo] = useState<any>();
  const [lives, setLives] = useState<LiveItem[]>([]);
  const [imageLoadResults, setImageLoadResults] = useState<
    Record<string, ImageLoadStatus>
  >({});
  const [activePage, setActivePage] = useState(ALL_PAGES);
  const [linkTestResults, setLinkTestResults] = useState<
    Record<string, LinkTestStatus>
  >({});
  const [testingLinks, setTestingLinks] = useState(false);
  const [liveSort, setLiveSort] = useState<LiveSort>("watchNumber");
  const [current, setCurrent] = useState<LiveItem>();
  const [page, setPage] = useState(1);
  const [groupIdFilter, setGroupIdFilter] = useState("");
  const [user, setUser] = useState("");
  const [userInfo, setUserInfo] = useState<any>();
  const [loadingUser, setLoadingUser] = useState(false);
  const [message, setMessage] = useState("");
  const [gifts, setGifts] = useState<any[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [timClient, setTimClient] = useState<TimClient>();

  const containerRef = useRef<HTMLDivElement>(null);
  const jessibucaPlayerRef = useRef<any>(null);
  const currentRef = useRef<LiveItem | undefined>(current);
  const msgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);
  const imageLoadResultsRef = useRef<Record<string, ImageLoadStatus>>({});
  const imageLoadGenerationRef = useRef(0);

  // 获取平台列表
  const getPlats = async () => {
    try {
      const resp = await request("/common/apis/v1/plats", { hasLive: true });
      if (resp && resp.code === 0) {
        setPlats(resp.data);
        const defaultPlat = resp.data.find(
          (item: PlatItem) => item.name === DEFAULT_PLAT,
        );
        if (defaultPlat?.info) {
          setPlatInfo(defaultPlat.info);
        }
      }
    } catch (error) {
      console.error("Failed to load plats:", error);
    }
  };

  // 初始化TIM客户端
  const initTim = (sdkAppId: number) => {
    if (!sdkAppId) return;
    const client = new TimClient(sdkAppId, "", "");

    // 监听消息
    client.on(TIM.EVENT.MESSAGE_RECEIVED, (event: any) => {
      const newMsgs: any[] = [];
      for (const data of event.data) {
        if (!data) continue;
        try {
          const msg = JSON.parse(data.payload.data);
          // console.log(msg)
          const types = [101, "0", "1", 107];
          if (types.indexOf(msg.type) > -1) {
            newMsgs.push(msg);
          }
        } catch (e) {
          // ignore
        }
      }
      if (newMsgs.length > 0) {
        setMsgs((prev) => [...prev, ...newMsgs]);
      }
    });

    setTimClient(client);
  };

  // 获取直播列表
  const getLives = async (platName: string, pageNum: number, clear = false) => {
    if (!platName) return;
    try {
      const resp = await request("/common/apis/v1/get_news_lives", {
        plat: platName,
        page_num: pageNum,
        page_size: 10,
      });
      if (resp && resp.code === 0) {
        if (clear) {
          setLinkTestResults({});
          imageLoadGenerationRef.current += 1;
          imageLoadResultsRef.current = {};
          setImageLoadResults({});
          setLives(resp.data);
        } else {
          setLives((previousLives) => [...previousLives, ...resp.data]);
        }
        setPage(pageNum + 1);
      }
    } catch (error) {
      console.error("Failed to load lives:", error);
    }
  };

  const loadLiveImage = (live: LiveItem): Promise<ImageLoadStatus> =>
    new Promise((resolve) => {
      if (!live.liveImage?.trim()) {
        resolve("invalid");
        return;
      }

      const image = new Image();
      const timeoutId = window.setTimeout(() => {
        image.onload = null;
        image.onerror = null;
        image.src = "";
        resolve("invalid");
      }, IMAGE_LOAD_TIMEOUT);

      image.onload = () => {
        window.clearTimeout(timeoutId);
        resolve(
          image.naturalWidth > 0 && image.naturalHeight > 0
            ? "valid"
            : "invalid",
        );
      };
      image.onerror = () => {
        window.clearTimeout(timeoutId);
        resolve("invalid");
      };
      image.decoding = "async";
      image.src = live.liveImage;
    });

  const testLiveLink = async (live: LiveItem): Promise<LinkTestStatus> => {
    let url = "";
    try {
      url = decode(live.nplayFlv);
      if (!url) {
        return { status: "invalid", reason: "链接为空" };
      }
    } catch {
      return { status: "invalid", reason: "链接解码失败" };
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      LINK_TEST_TIMEOUT,
    );
    const startedAt = performance.now();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Range: "bytes=0-1" },
        cache: "no-store",
        signal: controller.signal,
      });
      const latency = Math.round(performance.now() - startedAt);
      await response.body?.cancel();

      if (!response.ok && response.status !== 206) {
        return {
          status: "invalid",
          reason: `HTTP ${response.status}`,
        };
      }
      return { status: "available", latency };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return { status: "invalid", reason: "连接超时" };
      }
      // 浏览器无法区分 CORS 拦截和网络失败，避免把可播放链接误判为失效。
      return { status: "restricted" };
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const testVisibleLinks = async () => {
    if (testingLinks || filteredLives.length === 0) return;

    setTestingLinks(true);
    const testingResults = Object.fromEntries(
      filteredLives.map((live) => [
        getLiveKey(live),
        { status: "testing" } satisfies LinkTestStatus,
      ]),
    );
    setLinkTestResults((previous) => ({
      ...previous,
      ...testingResults,
    }));

    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < filteredLives.length) {
        const live = filteredLives[nextIndex++];
        const key = getLiveKey(live);
        const result = await testLiveLink(live);
        setLinkTestResults((previous) => ({
          ...previous,
          [key]: result,
        }));
      }
    };

    try {
      await Promise.all(
        Array.from(
          {
            length: Math.min(LINK_TEST_CONCURRENCY, filteredLives.length),
          },
          () => worker(),
        ),
      );
      setLiveSort("latency");
    } finally {
      setTestingLinks(false);
    }
  };

  const loadJessibucaScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.Jessibuca) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "/jessibuca/jessibuca.js";
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      document.head.appendChild(script);
    });
  };

  // 播放直播流
  const playVideo = async (url: string, targetLiveKey?: string) => {
    if (!containerRef.current) return;

    try {
      await loadJessibucaScript();
    } catch (e) {
      console.error("Failed to load Jessibuca script:", e);
      addLog("播放失败: 无法加载 Jessibuca 脚本");
      return;
    }

    if (!window.Jessibuca) {
      addLog("播放失败: Jessibuca 未定义");
      return;
    }

    // 检查是否在此期间切换了直播间
    if (targetLiveKey && currentRef.current && getLiveKey(currentRef.current) !== targetLiveKey) {
      return;
    }

    // 清理旧的播放器（必须 await 异步销毁完成，防止旧 Worker / DOM 冲突影响新播放器）
    if (jessibucaPlayerRef.current) {
      const oldPlayer = jessibucaPlayerRef.current;
      jessibucaPlayerRef.current = null;
      try {
        if (typeof oldPlayer.destroy === "function") {
          await oldPlayer.destroy();
        }
      } catch (e) {
        console.error("Failed to cleanup old player:", e);
      }
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }

    // 再次检查是否在清理期间切换了直播间
    if (targetLiveKey && currentRef.current && getLiveKey(currentRef.current) !== targetLiveKey) {
      return;
    }

    console.log("Playing with Jessibuca:", url);
    try {
      const player = new window.Jessibuca({
        container: containerRef.current,
        videoBuffer: 0.2, // 低延迟 200ms
        isResize: true,
        text: "",
        loadingText: "加载中...",
        debug: false,
        showBandwidth: false,
        operateBtns: {
          fullscreen: true,
          screenshot: true,
          play: true,
          audio: true,
        },
        forceNoOffscreen: true,
        isFlv: true,
        decoder: "/jessibuca/decoder.js",
        isMute: true,
      });

      player.on("error", (errorType: string, errorDetail: string) => {
        console.error("Jessibuca播放错误:", errorType, errorDetail);
        addLog(`播放错误: ${errorType} ${errorDetail || ""}`);
      });

      player.on("timeout", (type: string) => {
        console.warn("Jessibuca超时:", type);
        addLog(`播放超时: ${type}`);
      });

      player.on("start", () => {
        addLog("开始播放");
      });

      const playPromise = player.play(url);
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((e: any) => {
          console.error("Play failed:", e);
          addLog("播放失败: " + (e?.message || e));
        });
      }

      jessibucaPlayerRef.current = player;
      return player;
    } catch (error: any) {
      console.error("Failed to play video with Jessibuca:", error);
      addLog("播放失败: " + (error?.message || error));
    }
  };

  // 搜索直播间
  const searchLive = async () => {
    try {
      const resp = await request("/system/api/query/lives", {
        query_filter: { groupId: groupIdFilter },
        page_num: 1,
        page_size: 10,
      });
      if (resp && resp.code === 0) {
        setActivePage(ALL_PAGES);
        setLinkTestResults({});
        imageLoadGenerationRef.current += 1;
        imageLoadResultsRef.current = {};
        setImageLoadResults({});
        setLives(resp.data.list);
        // 获取平台信息
        if (resp.data.list.length > 0) {
          const firstLive = resp.data.list[0];
          const matchedPlat = plats.find((p) => p.name === firstLive.plat);
          if (matchedPlat?.info) {
            setPlatInfo(matchedPlat.info);
            initTim(parseInt(matchedPlat.info.live_config.tim_sdkappid));
          }
        }
      }
    } catch (error) {
      console.error("Failed to search lives:", error);
    }
  };

  // 加载用户
  const loadUser = async () => {
    if (!user) return;
    setLoadingUser(true);
    try {
      const platName = user.slice(0, 2);
      const resp = await request("/common/apis/v1/detail", {
        plat: platName,
        name: user,
        duration: 30,
      });
      if (resp && resp.code === 0) {
        setUserInfo(resp.data);
        addLog("加载账号成功");
        loadGifts(platName, user);
      } else {
        addLog("加载账号失败: " + resp.msg);
      }
    } catch (error) {
      addLog("加载账号失败");
    } finally {
      setLoadingUser(false);
    }
  };

  // 加载礼物列表
  const loadGifts = async (platName: string, userName: string) => {
    try {
      const resp = await request("/common/apis/v1/listProp", {
        plat: platName,
        name: userName,
      });
      if (resp && resp.code === 0) {
        setGifts(resp.data);
        addLog("加载礼物成功");
      } else {
        addLog("加载礼物失败");
      }
    } catch (error) {
      addLog("加载礼物失败");
    }
  };

  // 发送消息
  const sendMessage = async () => {
    if (!userInfo || !current || !message) return;
    const platName = user.slice(0, 2);
    try {
      const resp = await request("/common/apis/v1/group_msg", {
        plat: platName,
        name: user,
        msg: message,
        room_id: current.id,
      });
      if (resp && resp.code === 0) {
        addLog("发送消息成功");
        setMessage("");
      } else {
        addLog("发送消息失败: " + resp.msg);
      }
    } catch (error) {
      addLog("发送消息失败");
    }
  };

  // 发送礼物
  const sendGift = async (propId: string) => {
    if (!userInfo || !current) return;
    const platName = user.slice(0, 2);
    try {
      const resp = await request("/common/apis/v1/pop_prop", {
        plat: platName,
        name: user,
        prop_id: propId,
        room_id: current.id,
      });
      if (resp && resp.code === 0) {
        addLog("发送礼物成功");
      } else {
        addLog("发送礼物失败: " + resp.msg);
      }
    } catch (error) {
      addLog("发送礼物失败");
    }
  };

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev, msg]);
  };

  // 格式化消息
  const formatMessage = (msg: any) => {
    switch (msg.type) {
      case 101:
        return (
          <span className={msg.sender ? "text-yellow-400" : "text-gray-300"}>
            ({msg.sender?.user_id || ""}) ({msg.sender?.user_level || ""}){" "}
            {msg.data?.msg || ""}
          </span>
        );
      case "0":
        return (
          <span>
            {msg.sender?.nick_name}({msg.sender?.user_level || ""}): {msg.text}
          </span>
        );
      case "1":
        return (
          <span>
            {msg.sender?.user_id}: {msg.top_title}
          </span>
        );
      case 107:
        return <span>{msg.sender?.nick_name} 进入直播间</span>;
      default:
        return <span className="text-gray-500 text-xs">未知消息</span>;
    }
  };

  useEffect(() => {
    getPlats().then(() => getLives(DEFAULT_PLAT, 1, true));
  }, []);

  useEffect(() => {
    const pendingLives = lives.filter(
      (live) => !imageLoadResultsRef.current[getLiveKey(live)],
    );
    if (pendingLives.length === 0) return;

    const generation = imageLoadGenerationRef.current;
    const loadingResults: Record<string, ImageLoadStatus> = Object.fromEntries(
      pendingLives.map((live) => [
        getLiveKey(live),
        "loading",
      ]),
    );
    imageLoadResultsRef.current = {
      ...imageLoadResultsRef.current,
      ...loadingResults,
    };
    setImageLoadResults(imageLoadResultsRef.current);

    let nextIndex = 0;
    const worker = async () => {
      while (
        generation === imageLoadGenerationRef.current &&
        nextIndex < pendingLives.length
      ) {
        const live = pendingLives[nextIndex++];
        const result = await loadLiveImage(live);
        if (generation !== imageLoadGenerationRef.current) return;
        imageLoadResultsRef.current = {
          ...imageLoadResultsRef.current,
          [getLiveKey(live)]: result,
        };
        setImageLoadResults(imageLoadResultsRef.current);
      }
    };

    Promise.all(
      Array.from(
        {
          length: Math.min(IMAGE_LOAD_CONCURRENCY, pendingLives.length),
        },
        () => worker(),
      ),
    );
  }, [lives]);

  // 暂不通过图片预加载过滤播放列表
  const validLives = useMemo(() => lives, [lives]);

  const loadingImageCount = useMemo(
    () =>
      lives.filter(
        (live) => imageLoadResults[getLiveKey(live)] === "loading",
      ).length,
    [imageLoadResults, lives],
  );

  const pageTabs = useMemo(() => {
    const counts = new Map<string, number>();
    validLives.forEach((live) => {
      const page = normalizePage(live.page);
      if (page) {
        counts.set(page, (counts.get(page) || 0) + 1);
      }
    });
    return Array.from(counts, ([value, count]) => ({ value, count })).sort(
      (a, b) =>
        a.value.localeCompare(b.value, undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    );
  }, [validLives]);

  const filteredLives = useMemo(
    () =>
      activePage === ALL_PAGES
        ? validLives
        : validLives.filter(
            (live) => normalizePage(live.page) === activePage,
          ),
    [activePage, validLives],
  );

  const sortedLives = useMemo(() => {
    const statusRank: Record<LinkTestStatus["status"] | "untested", number> = {
      available: 0,
      testing: 1,
      restricted: 2,
      invalid: 3,
      untested: 4,
    };

    return filteredLives
      .map((live, originalIndex) => ({ live, originalIndex }))
      .sort((a, b) => {
        if (liveSort === "watchNumber") {
          return (
            b.live.watchNumber - a.live.watchNumber ||
            a.originalIndex - b.originalIndex
          );
        }

        const aResult = linkTestResults[getLiveKey(a.live)];
        const bResult = linkTestResults[getLiveKey(b.live)];
        const aStatus = aResult?.status || "untested";
        const bStatus = bResult?.status || "untested";
        const rankDifference = statusRank[aStatus] - statusRank[bStatus];
        if (rankDifference !== 0) return rankDifference;

        if (
          aResult?.status === "available" &&
          bResult?.status === "available"
        ) {
          return (
            aResult.latency - bResult.latency ||
            b.live.watchNumber - a.live.watchNumber ||
            a.originalIndex - b.originalIndex
          );
        }

        return (
          b.live.watchNumber - a.live.watchNumber ||
          a.originalIndex - b.originalIndex
        );
      })
      .map(({ live }) => live);
  }, [filteredLives, linkTestResults, liveSort]);

  useEffect(() => {
    if (
      activePage !== ALL_PAGES &&
      !pageTabs.some(({ value }) => value === activePage)
    ) {
      setActivePage(ALL_PAGES);
    }
  }, [activePage, pageTabs]);

  // 平台信息变化时初始化TIM
  useEffect(() => {
    if (platInfo?.live_config?.tim_sdkappid) {
      initTim(parseInt(platInfo.live_config.tim_sdkappid));
    }
  }, [platInfo]);

  // 当前直播间变化时播放视频并加入群组
  useEffect(() => {
    if (!current) {
      // 如果没有当前直播间，清理播放器
      if (jessibucaPlayerRef.current) {
        try {
          jessibucaPlayerRef.current.destroy();
          jessibucaPlayerRef.current = null;
        } catch (error) {
          console.error("Failed to destroy player:", error);
        }
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      return;
    }

    console.log("Current live changed:", current);
    // 解码并播放视频
    try {
      const url = decode(current.nplayFlv);
      playVideo(url, getLiveKey(current));
    } catch (error) {
      console.error("Failed to decode video url:", error);
      addLog("播放失败: 无法解码视频URL");
    }

    // 加入群组
    if (timClient) {
      timClient.addGroup(current.groupId);
      addLog("加入直播间: " + current.hostName);
    }

    // 清理函数
    return () => {
      // 退出群组
      if (timClient && current) {
        timClient.quitGroup(current.groupId);
      }
      // 清空消息
      setMsgs([]);
    };
  }, [current, timClient]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      // 清理播放器
      if (jessibucaPlayerRef.current) {
        try {
          console.log("Component unmounting, cleaning up player");
          jessibucaPlayerRef.current.destroy();
          jessibucaPlayerRef.current = null;
        } catch (error) {
          console.error("Failed to cleanup player on unmount:", error);
        }
      }
    };
  }, []);

  // 消息滚动到底部
  useEffect(() => {
    if (msgRef.current) {
      msgRef.current.scrollTop = msgRef.current.scrollHeight;
    }
  }, [msgs]);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape" && current) {
        e.preventDefault();
        setCurrent(undefined);
      }
      if (e.code === "Space" && jessibucaPlayerRef.current) {
        e.preventDefault();
        try {
          if (jessibucaPlayerRef.current.isMute()) {
            jessibucaPlayerRef.current.cancelMute();
          } else {
            jessibucaPlayerRef.current.mute();
          }
        } catch (err) {
          console.error("Volume toggle error:", err);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [current]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* 顶部工具栏 */}
      <div className="bg-black/50 backdrop-blur-md border-b border-gray-700 p-4 sticky top-0 z-10">
        <div className="flex gap-3 flex-wrap items-center">
          <Select
            value={plat}
            onValueChange={(value) => {
              const selectedPlat = plats.find((p) => p.name === value);
              setPlat(value);
              setActivePage(ALL_PAGES);
              if (selectedPlat?.info) {
                setPlatInfo(selectedPlat.info);
              }
              getLives(value, 1, true);
            }}
          >
            <SelectTrigger className="w-40 bg-gray-800 border-gray-600">
              <SelectValue placeholder="选择平台" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600">
              {plats.map((p) => (
                <SelectItem key={p.name} value={p.name} className="text-white">
                  {p.name} ({p.lives?.length || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            className="w-48 bg-gray-800 border-gray-600 text-white"
            placeholder="搜索直播间ID"
            value={groupIdFilter}
            onChange={(e) => setGroupIdFilter(e.target.value)}
          />
          <Button
            onClick={searchLive}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            搜索
          </Button>

          <div className="flex gap-2 ml-auto">
            <Input
              className="w-48 bg-gray-800 border-gray-600 text-white"
              placeholder="输入账号名称"
              value={user}
              onChange={(e) => setUser(e.target.value)}
            />
            <Button
              disabled={loadingUser}
              onClick={() => {
                if (userInfo) {
                  setUser("");
                  setUserInfo(undefined);
                  setGifts([]);
                } else {
                  loadUser();
                }
              }}
              variant={userInfo ? "destructive" : "default"}
              className={
                !userInfo
                  ? "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                  : ""
              }
            >
              {loadingUser ? "加载中..." : userInfo ? "卸载账号" : "加载账号"}
            </Button>
            {userInfo && (
              <div className="flex items-center px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-md font-medium">
                💰 ¥{userInfo.total_account?.toFixed(2) || "0.00"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 主内容区 - 三栏布局 */}
      <div className="flex h-[calc(100vh-73px)]">
        {/* 第一栏：视频播放器 */}
        {current && (
          <div className="w-96 border-r border-gray-700 bg-black flex flex-col flex-shrink-0">
            {/* 视频播放器 - 占满整个高度 */}
            <div className="flex-1 flex items-center justify-center">
              <div className="relative w-full h-full bg-black overflow-hidden">
                <div
                  ref={containerRef}
                  className="w-full h-full relative"
                />

                {/* 顶部主播信息 */}
                <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent z-10 pointer-events-none">
                  <div className="flex items-center gap-3 pointer-events-auto">
                    <img
                      src={current.liveImage}
                      alt={current.hostName}
                      className="w-10 h-10 rounded-full border-2 border-pink-500"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-sm truncate">
                        {current.hostName}
                      </h3>
                      <p className="text-xs text-gray-300 truncate">
                        {current.title}
                      </p>
                    </div>
                    <div className="bg-red-600/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      {current.watchNumber}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 第二栏：控制面板、弹幕和操作日志 */}
        {current && (
          <div className="w-80 border-r border-gray-700 bg-gray-900 flex flex-col flex-shrink-0">
            {/* 关闭按钮和控制区 */}
            <div className="p-4 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold">直播控制</h3>
                <button
                  onClick={() => setCurrent(undefined)}
                  className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 transition-all flex items-center justify-center text-white"
                >
                  ✕
                </button>
              </div>

              {/* 礼物快捷按钮 */}
              {userInfo && gifts.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-semibold mb-2 text-gray-400">
                    快捷礼物
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {gifts.slice(0, 4).map((gift) => (
                      <button
                        key={gift.id}
                        onClick={() => sendGift(gift.id)}
                        className="flex flex-col items-center gap-1 p-1.5 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all"
                      >
                        <div className="relative">
                          <img
                            src={gift.icon}
                            alt={gift.name}
                            className="w-8 h-8"
                          />
                          <div className="absolute -bottom-0.5 -right-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded-full w-3 h-3 flex items-center justify-center">
                            {gift.diamonds}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* 弹幕区域 */}
            <div ref={msgRef} className="flex-1 min-h-0 overflow-y-auto p-4">
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                直播弹幕
              </h3>
              <div className="space-y-2">
                {msgs.map((msg, i) => (
                  <div
                    key={i}
                    className="bg-gray-800/50 backdrop-blur-sm px-3 py-2 rounded-lg text-xs text-white"
                  >
                    {formatMessage(msg)}
                  </div>
                ))}
              </div>
            </div>

            {/* 发送消息区域 */}
            {userInfo && (
              <div className="p-4 border-t border-gray-800 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="说点什么..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") sendMessage();
                    }}
                    className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-400 h-9 text-sm"
                  />
                  <Button
                    onClick={sendMessage}
                    size="sm"
                    className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                  >
                    发送
                  </Button>
                </div>
              </div>
            )}

            {/* 操作日志 */}
            {logs.length > 0 && (
              <div className="p-4 border-t border-gray-800 max-h-48 overflow-y-auto flex-shrink-0">
                <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  操作日志
                </h3>
                <div className="space-y-1">
                  {logs.slice(-10).map((log, i) => (
                    <p
                      key={i}
                      className="text-xs text-gray-400 border-l-2 border-gray-700 pl-2"
                    >
                      {log}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 第三栏：直播列表 */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {lives.length > 0 && (
            <div className="z-10 flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-gray-700 bg-gray-900/95 px-6 py-4 backdrop-blur-md">
              <Tabs
                value={activePage}
                onValueChange={setActivePage}
                className="min-w-0 flex-1"
              >
                <TabsList className="h-auto max-w-full justify-start gap-1 overflow-x-auto bg-gray-800 p-1">
                  <TabsTrigger
                    value={ALL_PAGES}
                    className="text-gray-300 data-[state=active]:bg-gray-600 data-[state=active]:text-white"
                  >
                    全部 ({validLives.length})
                  </TabsTrigger>
                  {pageTabs.map(({ value, count }) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className="text-gray-300 data-[state=active]:bg-gray-600 data-[state=active]:text-white"
                    >
                      {value} ({count})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <Select
                value={liveSort}
                onValueChange={(value) => setLiveSort(value as LiveSort)}
              >
                <SelectTrigger className="w-36 border-gray-600 bg-gray-800 text-white">
                  <SelectValue placeholder="排序方式" />
                </SelectTrigger>
                <SelectContent className="border-gray-600 bg-gray-800">
                  <SelectItem value="watchNumber" className="text-white">
                    观看人数
                  </SelectItem>
                  <SelectItem value="latency" className="text-white">
                    延迟优先
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                disabled={testingLinks || filteredLives.length === 0}
                onClick={testVisibleLinks}
                className="border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
              >
                {testingLinks ? "检测中..." : "检测直播链接"}
              </Button>
              {loadingImageCount > 0 && (
                <span className="text-xs text-gray-400">
                  正在校验图片：{loadingImageCount}
                </span>
              )}
            </div>
          )}

          <div className={`flex-1 overflow-y-auto ${current ? "p-4" : "p-6"}`}>
            <div
              className={`grid ${
                current
                  ? "grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3"
                  : "grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
              }`}
            >
              {sortedLives.map((live) => {
                const linkTestResult = linkTestResults[getLiveKey(live)];
                return (
	                  <div
	                    key={getLiveKey(live)}
	                    className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
	                    onClick={() => setCurrent(live)}
	                  >
	                    <div className="relative rounded-xl overflow-hidden bg-gray-800 shadow-lg">
	                      <img
	                        src={live.liveImage}
	                        alt={live.title}
	                        className={`w-full object-cover ${current ? "aspect-[3/4]" : "aspect-[9/12]"}`}
	                        onError={() => {
	                          const key = getLiveKey(live);
	                          imageLoadResultsRef.current = {
                            ...imageLoadResultsRef.current,
                            [key]: "invalid",
                          };
                          setImageLoadResults(imageLoadResultsRef.current);
                          if (current && getLiveKey(current) === key) {
                            // 暂不清空当前直播间
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

	                      {/* 观看人数标签 */}
	                      <div
	                        className={`absolute right-2 top-2 flex items-center gap-1 rounded-full bg-red-600/90 font-bold backdrop-blur-sm ${
	                          current
	                            ? "px-2 py-0.5 text-[10px]"
	                            : "px-3 py-1 text-xs"
	                        }`}
	                      >
	                        <span
	                          className={`rounded-full bg-white animate-pulse ${
	                            current ? "h-1.5 w-1.5" : "h-2 w-2"
	                          }`}
	                        />
	                        {live.watchNumber}
	                      </div>

	                      {linkTestResult && (
	                        <div
	                          className={`absolute left-2 top-2 rounded-full font-bold backdrop-blur-sm ${
	                            current
	                              ? "px-2 py-0.5 text-[10px]"
	                              : "px-2.5 py-1 text-xs"
	                          } ${
	                            linkTestResult.status === "available"
	                              ? "bg-green-600/90"
	                              : linkTestResult.status === "testing"
	                                ? "bg-blue-600/90"
	                                : linkTestResult.status === "restricted"
	                                  ? "bg-yellow-600/90"
	                                  : "bg-red-700/90"
                          }`}
                        >
                          {linkTestResult.status === "available" &&
                            `可用 ${linkTestResult.latency}ms`}
                          {linkTestResult.status === "testing" && "检测中"}
                          {linkTestResult.status === "restricted" &&
                            "检测受限"}
                          {linkTestResult.status === "invalid" &&
                            `失效：${linkTestResult.reason}`}
                        </div>
                      )}

	                      {/* 底部信息 */}
	                      <div
	                        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent ${
	                          current ? "p-2" : "p-3"
	                        }`}
	                      >
	                        <p
	                          className={`truncate font-bold ${
	                            current ? "text-xs" : "text-sm"
	                          }`}
	                        >
	                          {live.hostName}
	                        </p>
	                        <p
	                          className={`truncate text-gray-300 ${
	                            current ? "text-[10px]" : "text-xs"
	                          }`}
	                        >
	                          {live.title}
	                        </p>
	                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 加载更多 */}
            {plat && lives.length > 0 && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  onClick={() => getLives(plat, page)}
                  className="bg-gray-800 border-gray-600 hover:bg-gray-700 text-white px-8"
                >
                  ⬇️ 加载更多直播间
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
